import { Suspense, lazy } from 'react'
import { Route, Routes, useLocation } from 'react-router'
import { useAuth } from './auth/context'
import { AppShell } from './components/AppShell'
import { ManualCopyHost } from './components/ManualCopy'
import { PostOverlay } from './components/post/PostOverlay'
import { Toaster } from './components/Toast'
import type { OverlayState } from './lib/feedNav'
import { NavigationProvider } from './lib/navigation'
import { FavoriteGroupPage } from './pages/FavoriteGroupPage'
import { FavoritesPage } from './pages/FavoritesPage'
import { FeedPage } from './pages/FeedPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { PostPage } from './pages/PostPage'
import { SettingsPage } from './pages/SettingsPage'
import { SourcesPage } from './pages/SourcesPage'
import { SourcePage } from './pages/SourcePage'
import { ToolsPage } from './pages/ToolsPage'
import { UniquenessPage } from './pages/UniquenessPage'
import { BootScreen } from './screens/BootScreen'
import { ErrorScreen } from './screens/ErrorScreen'
import { LoginScreen } from './screens/LoginScreen'
import { NoAccessScreen } from './screens/NoAccessScreen'
import { Spinner } from './components/Spinner'
import { useKeyboardSwipeDismiss } from './lib/useKeyboardSwipeDismiss'

// Контент-план — отдельным чанком: перетаскивание и редактор Telegram в основной не попадают.
const PlanPage = lazy(() => import('./pages/PlanPage'))
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'))

function AnalyticsRoute() {
  return <Suspense fallback={<div className="flex min-h-64 items-center justify-center"><Spinner size={24} /></div>}><AnalyticsPage /></Suspense>
}

function PlanRoute() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-64 items-center justify-center">
          <Spinner size={24} />
        </div>
      }
    >
      <PlanPage />
    </Suspense>
  )
}

export function App() {
  const { state } = useAuth()
  useKeyboardSwipeDismiss()

  switch (state.status) {
    case 'loading':
      return <BootScreen />
    case 'signed_out':
      return <LoginScreen />
    case 'no_access':
      return <NoAccessScreen hadInvite={state.hadInvite} />
    case 'error':
      return <ErrorScreen kind={state.kind} />
    case 'signed_in':
      return <NavigationProvider key={`${state.user.telegram_id}:${state.user.role}`}><SignedIn /></NavigationProvider>
  }
}

/**
 * Карточка, открытая из ленты, рисуется поверх неё: лента остаётся смонтированной
 * под `backgroundLocation`, поэтому после закрытия прокрутка и загруженные страницы
 * на месте. Прямая ссылка /post/:id открывает карточку обычной страницей.
 */
function SignedIn() {
  const location = useLocation()
  const background = (location.state as OverlayState | null)?.backgroundLocation

  return (
    <>
      <div className="contents" data-view-entry={(background ?? location).key}>
      <Routes location={background ?? location}>
        <Route element={<AppShell />}>
          <Route index element={<FeedPage />} />
          <Route path="post/:id" element={<PostPage />} />
          <Route path="favorites" element={<FavoritesPage />} />
          <Route path="favorites/:groupId" element={<FavoriteGroupPage />} />
          <Route path="tools" element={<ToolsPage />} />
          <Route path="tools/uniqueness" element={<UniquenessPage />} />
          <Route path="sources" element={<SourcesPage />} />
          <Route path="sources/:id" element={<SourcePage />} />
          <Route path="plan" element={<PlanRoute />} />
          <Route path="analytics" element={<AnalyticsRoute />} />
          <Route path="analytics/reels/:shortcode" element={<AnalyticsRoute />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      </div>
      {background && (
        <div className="contents" data-view-entry={location.key}>
        <Routes>
          <Route path="post/:id" element={<PostOverlay />} />
        </Routes>
        </div>
      )}
      <Toaster />
      <ManualCopyHost />
    </>
  )
}
